/* eslint-disable comma-dangle */
import axios from "axios";
import { Message, MessageBox } from "element-ui";
import store from "@/store";
import {
  getToken,
  setToken,
  removeToken,
  isCurrentTokenValid,
  shouldRefreshCurrentToken,
} from "@/utils/auth";
import router from "@/router";

// 创建 axios 实例
const service = axios.create({
  baseURL: process.env.VUE_APP_BASE_API ?? "", // api 的 base_url
  timeout: 15000, // 请求超时时间
});

// 防止多个401响应同时弹出多个登录过期对话框
let isShowingLoginExpired = false;

// Token刷新状态控制：防止并发请求同时触发多次刷新
let isRefreshing = false;
let refreshSubscribers = [];

// request 拦截器
service.interceptors.request.use(
  config => {
    // 在发送请求之前做些什么
    const token = getToken();
    if (token && isCurrentTokenValid()) {
      // 让每个请求携带自定义token 请根据实际情况自行修改
      config.headers["Authorization"] = "Bearer " + token;

      // token即将过期时自动刷新（剩余不到5分钟）
      if (shouldRefreshCurrentToken() && !isRefreshing) {
        isRefreshing = true;
        import("@/api/user")
          .then(({ refreshToken }) => refreshToken())
          .then((res) => {
            const newToken = res.data.token;
            if (newToken) {
              setToken(newToken);
              refreshSubscribers.forEach((cb) => cb(newToken));
            }
          })
          .catch(() => {
            // 刷新失败不影响当前请求，token过期后会跳转登录
          })
          .finally(() => {
            isRefreshing = false;
            refreshSubscribers = [];
          });
      }
    } else if (token && !isCurrentTokenValid()) {
      removeToken();
      store.dispatch("user/resetUser");
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// response 拦截器
service.interceptors.response.use(
  response => {
    // 二进制响应（如 PDF 下载）直接返回
    if (response.config.responseType === "blob" || response.config.responseType === "arraybuffer") {
      return response;
    }

    const res = response.data;

    // 接口请求成功
    if (res.success === true && (res.code === 200 || res.code === 201 || res.code === 0)) {
      return res;
    } else {
      // 对于登录和退出登录相关的错误，不在这里显示消息
      const isAuthRequest =
        response.config.url.includes("/auth/login") || response.config.url.includes("/auth/logout");

      if (!isAuthRequest) {
        // 非认证请求的错误，在这里显示
        Message({
          message: res.message || "Error",
          type: "error",
          duration: 5 * 1000,
        });
      }

      // 50008:非法的token; 50012:其他客户端登录了;  50014:Token 过期了;
      if (res.code === 50008 || res.code === 50012 || res.code === 50014) {
        // 清除本地状态
        removeToken();
        store.dispatch("user/resetUser");

        // 防止多个请求同时弹出多个对话框
        if (!isShowingLoginExpired) {
          isShowingLoginExpired = true;
          MessageBox.confirm("登录状态已过期，请重新登录", "登录过期", {
            confirmButtonText: "重新登录",
            cancelButtonText: "取消",
            type: "warning",
          })
            .then(() => {
              router.push({
                path: "/login",
                query: { redirect: router.currentRoute.fullPath },
              });
            })
            .catch(() => {
              router.push("/login");
            })
            .finally(() => {
              isShowingLoginExpired = false;
            });
        }
      }

      // 创建一个包含完整错误信息的错误对象
      const error = new Error(res.message || "Error");
      error.response = response;
      error.data = res;
      return Promise.reject(error);
    }
  },
  error => {
    // 处理HTTP 401未授权错误（token过期或无效）
    if (error.response && error.response.status === 401) {
      const token = getToken();
      if (token) {
        removeToken();
        store.dispatch("user/resetUser");

        const code = error.response.data?.code;
        const isKickedOut = code === 50012;
        const dialogTitle = isKickedOut ? "账号已在其他设备登录" : "登录过期";
        const dialogMsg = isKickedOut
          ? "您的账号已在其他设备登录，当前会话已失效，请重新登录"
          : "登录状态已过期，请重新登录";

        // 防止多个请求同时弹出多个对话框
        if (!isShowingLoginExpired) {
          isShowingLoginExpired = true;
          MessageBox.confirm(dialogMsg, dialogTitle, {
            confirmButtonText: "重新登录",
            cancelButtonText: "取消",
            type: "warning",
          })
            .then(() => {
              router.push({
                path: "/login",
                query: { redirect: router.currentRoute.fullPath },
              });
            })
            .catch(() => {
              router.push("/login");
            })
            .finally(() => {
              isShowingLoginExpired = false;
            });
        }
      }
      return Promise.reject(error);
    }

    // 对于登录和退出登录相关的错误，不在这里显示消息
    const isAuthRequest =
      error.config &&
      error.config.url &&
      (error.config.url.includes("/auth/login") || error.config.url.includes("/auth/logout"));

    if (!isAuthRequest) {
      Message({
        message: error.message,
        type: "error",
        duration: 5 * 1000,
      });
    }

    return Promise.reject(error);
  }
);

export default service;
